import { Injectable } from '@angular/core';
import {
  IEntityConfig,
  ENTITY_TYPES,
  BackendApiType,
  ObjectId,
  IFilterQuery,
  IBaseEntityResponse,
  IApiListResponse,
  IBaseEntityType,
} from '@app/shared/types';
import { BaseService } from '../base';
import { Observable, rx } from '@shared/rx';
import { batchRequestMap } from '@shared/rx-operators';
import {
  difference,
  filter,
  get,
  has,
  includes,
  isEmpty,
  lte,
  keyBy,
  map,
  merge,
  omit,
  set,
  size,
  isFunction,
} from 'lodash';
import { MAX_URL_QUERY_NUMBER } from '@config/global-constants';
import { observableOf } from '@app/shared/rx/rx-wrapper';

const entityType: ENTITY_TYPES = ENTITY_TYPES.INVENTORY_ITEM_VENUE;
const config: IEntityConfig = {
  backendApiType: BackendApiType.DM,
  ENTITY_TYPE: entityType,
  isMasterListAvailable: true,
  apiEndpoint: entityType,
  uiRoute: entityType,
  customEntityRoute: {
    list: 'master',
  },
  buttons: {},
};

@Injectable()
export class InventoryItemVenueService extends BaseService {
  public static config: IEntityConfig = config;
  public static ENTITY_TYPE: string = config.ENTITY_TYPE;
  private siteFields: string[] = [
    'site_id',
    'thresholds',
    'physical_count_measures',
  ];

  public batchFetchCurrentList(
    siteGroupIds: ObjectId[],
    params: IFilterQuery,
    options?: {
      filterMasterDocFunction?: (items: IBaseEntityType[]) => IBaseEntityType[];
    },
  ): Observable<IBaseEntityResponse<any>> {
    const selectFields: string[] = get(params, 'select', []);
    const masterParam = {
      ...omit(params, 'filter'),
      select: difference(selectFields, this.siteFields),
    };

    if (has(params, 'masterFilter')) {
      set(masterParam, 'filter', get(params, 'masterFilter'));
    }

    return this.fetchList(masterParam).pipe(
      rx.mergeMap((apiListResponse: IApiListResponse) => {
        const itemList = get(apiListResponse, 'items', []);
        const filteredItemList = isFunction(options?.filterMasterDocFunction)
          ? options.filterMasterDocFunction(itemList)
          : itemList;

        if (isEmpty(filteredItemList)) {
          return observableOf({
            items: [],
            total: 0,
          }) as Observable<IBaseEntityResponse<any>>;
        }
        /**
         * Build a lookup keyed by entity_id so merging current site items can use O(1) lookups instead of calling `find` for every item.
         * This avoids an O(n * m) nested-scan (n = master items, m = current items in the following usage line).
         * And reduces it to O(n + m) at the cost of O(n) additional memory for the map.
         */
        const masterDocsByEntityId = keyBy(filteredItemList, 'entity_id');

        if (lte(size(filteredItemList), MAX_URL_QUERY_NUMBER)) {
          const entityIds = map(filteredItemList, 'entity_id').join();
          const newFilter = `${get(params, 'filter', '')} and entity_id eq in(${entityIds})`;
          set(params, 'filter', newFilter);
        }

        return batchRequestMap(siteGroupIds, (groupId: ObjectId) =>
          this.fetchCurrentList(
            {
              ...omit(params, 'masterFilter'),
              select: filter(selectFields, (field: string) =>
                includes(this.siteFields, field),
              ),
            },
            [],
            groupId,
          ),
        ).pipe(
          rx.reduce(
            (totalResult, currentResult) => {
              const currentItems = map(
                get(currentResult, 'items', []),
                (currentItem) => {
                  return merge(
                    {},
                    currentItem,
                    masterDocsByEntityId[currentItem.entity_id],
                  );
                },
              );

              totalResult.items.push(...currentItems);
              totalResult.total += size(get(currentResult, 'items', []));

              return totalResult;
            },
            <IBaseEntityResponse>{ items: [], total: 0 },
          ),
        );
      }),
    );
  }
}
